import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, catchError, finalize, forkJoin, lastValueFrom, of, Subject, take, takeUntil } from 'rxjs';


import { PollService } from '../shared/services/poll.service';
import { IncentiveService } from "../shared/services/incentive.service";

import { ImageService } from "../../../../sapore-survey-pwa/src/app/shared/services/img-base64.service";
import { StepsEnum } from "../shared/enums/steps.enum";
import { PollTypeEnum } from "../shared/enums/poll-type.enum";
import { PollWebIntroComponent } from "./poll-web-intro/poll-web-intro.component";
import { LoadingService } from "../../../../sapore-survey-pwa/src/app/shared/services/loading.service";
import { PollManagerComponent } from 'projects/lib-ui/src/shared/components/poll-manager/poll-manager.component';
import { PageUnavailableComponent } from 'projects/lib-ui/src/shared/components/page-unavailable/page-unavailable.component';
import { IPartialUnit } from 'projects/lib-ui/src/shared/interfaces/unit';
import { IIncentive } from 'projects/lib-ui/src/shared/interfaces/incentive';
import { IPollResponse, IResponses } from 'projects/lib-ui/src/shared/interfaces/poll-responses';
import { INCENTIVE_IMG_DEFAULT } from 'projects/lib-ui/src/shared/constants/incentive-img-default.const';
import { resolveVoteCooldownMinutes } from '../shared/utils/vote-cooldown.util';

@Component({
  selector: 'app-poll-web',
  standalone: true,
  imports: [CommonModule, PollManagerComponent, PageUnavailableComponent, PollWebIntroComponent],
  templateUrl: './poll-web.component.html',
  styleUrls: ['./poll-web.component.scss'],
})
export class PollWebComponent implements OnInit, OnDestroy {
  answered = false;
  lockScreen = false;
  cooldownRemainingMinutes = 0;
  unitPoll!: IPartialUnit;
  incentive: IIncentive | null = null;
  interval!: any;
  pollIsLoadingDone = false;

  private readonly incentiveService = inject(IncentiveService);
  private readonly imageService = inject(ImageService);
  private readonly loadingService = inject(LoadingService);

  private readonly pollService = inject(PollService);

  private steps = new BehaviorSubject<StepsEnum>(StepsEnum.INTRO);

  steps$ = this.steps.asObservable();

  private unsubscribe = new Subject<void>()

  constructor(
    private readonly activatedRoute: ActivatedRoute,
  ) {
  }

  ngOnInit(): void {
    this.stepsInit();

    this.unitPoll = this.activatedRoute.snapshot.data['unitPoll'].data;
    this.checkAnswered();
    this.handlerSchedule();

    this.getIncentiveByBranchId(this.unitPoll.id);

    if (!this.unitPoll.pollDishReservationId && this.unitPoll.pollId) {
      this.steps.next(StepsEnum.POLL);
      this.pollGet(this.unitPoll.pollId!);
    }

  }

  ngOnDestroy(): void {
    clearInterval(this.interval);
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  async onFinishPoll(responses: IResponses[]): Promise<void> {
    const createdAt = new Date();

    const answers: IPollResponse = {
      id: '',
      branchId: this.unitPoll.id,
      pollId: this.unitPoll.poll!.id,
      deviceId: null,
      createdAt: new Date(
        createdAt.getTime() - 3 * 60 * 60 * 1000
      ).toISOString(),
      pollAnswers: responses,
    };

    try {
      const res = await lastValueFrom(
        this.pollService.sendPollResponse(answers).pipe(take(1))
      );

      if (res.data) {
        localStorage.setItem(
          'answered',
          `ts:${createdAt.toISOString()}`
        );
        setTimeout(() => {
          this.answered = true;
          this.checkAnswered();
        }, this.unitPoll.poll?.endPage.timer! * 2000);
      }
    } catch (error) {
      console.error('Erro ao enviar resposta da pesquisa:', error);
    }
  }

  checkAnswered(): void {
    const answered = localStorage.getItem('answered');
    const now = new Date();
    const cooldownMinutes = resolveVoteCooldownMinutes(
      this.unitPoll?.minVoteIntervalMinutes
    );
    this.cooldownRemainingMinutes = 0;

    if (answered) {
      const hasTimestampPrefix = answered.slice(0, 3) === 'ts:';
      const rawDateValue = hasTimestampPrefix
        ? answered.slice(3)
        : answered;
      const answeredDate = new Date(rawDateValue);

      if (isNaN(answeredDate.getTime())) {
        this.answered = false;
        localStorage.removeItem('answered');
        return;
      }

      const expirationDate = hasTimestampPrefix
        ? new Date(answeredDate.getTime() + cooldownMinutes * 60 * 1000)
        : answeredDate;

      if (expirationDate > now) {
        this.answered = true;
        const remainingMs = expirationDate.getTime() - now.getTime();
        this.cooldownRemainingMinutes = Math.max(1, Math.ceil(remainingMs / 60000));
      } else {
        this.answered = false;
        localStorage.removeItem('answered');
      }
    } else {
      this.answered = false;
    }
  }

  handlerSchedule(): void {
    let lockScreenItem = true;

    this.unitPoll.availableShifts.forEach((shift) => {
      const now = new Date();

      const [initialHoras, initialMinutos] = shift.startTime
        .split(':')
        .map(Number);

      const [endHoras, endMinutos] = shift.endTime.split(':').map(Number);

      const start = new Date(
        new Date().setHours(initialHoras, initialMinutos, 0, 0)
      );
      const end = new Date(new Date().setHours(endHoras, endMinutos, 0, 0));

      if (end < start || end === start) {
        end.setDate(end.getDate() + 1);
      }

      if (now >= start && now <= end) {
        lockScreenItem = false;
      }
    });

    if (!this.unitPoll.availableShifts.length) {
      lockScreenItem = false;
    }

    setTimeout(() => {
      this.lockScreen = lockScreenItem;
    }, this.unitPoll.poll?.endPage.timer ?? 2 * 1000);
  }

  getIncentiveByBranchId(branchId: string): void {
    this.incentiveService.getBranchById(branchId).subscribe({
      next: (res) => {

        if (!res) {
          this.incentive = null;
          this.pollIsLoadingDone = true;
          return;
        }

        const todayDate = new Date().toISOString().split('T')[0];
        const startDate = res.startDate.split('T')[0];
        const endDate = res.endDate.split('T')[0];

        if (startDate > todayDate || endDate < todayDate) {
          this.incentive = null;
          this.pollIsLoadingDone = true;
          return;
        }

        forkJoin({
          noticePageImg: this.imageService.convertToBase64(res.noticePageImg)
            .pipe(catchError(() => of('' as string))),
          winnerPageImg: this.imageService.convertToBase64(res.winnerPageImg)
            .pipe(catchError(() => of('' as string)))
        }).subscribe({
          next: ({ noticePageImg, winnerPageImg }) => {

            this.incentive = {
              ...res,
              noticePageImg: noticePageImg || INCENTIVE_IMG_DEFAULT,
              winnerPageImg: winnerPageImg || INCENTIVE_IMG_DEFAULT,
            };

            this.pollIsLoadingDone = true;
          }
        });
      },
      error: (err) => {
        console.error('Error fetching incentive:', err);
        this.pollIsLoadingDone = true;
      },
    });
  }

  introOnSelectedType(type: PollTypeEnum) {
    this.loadingService.showLoading();
    this.pollGet(type === PollTypeEnum.Satisfaction ? this.unitPoll.pollId! : this.unitPoll.pollDishReservationId!);
  }

  private stepsInit(): void {
    this.steps$.pipe(takeUntil(this.unsubscribe)).subscribe((step) => {
      if (step === StepsEnum.POLL) {
        this.intervalInit();
        return;
      }

      clearInterval(this.interval);
    });
  }

  private intervalInit(): void {
    this.interval = setInterval(() => {
      this.checkAnswered();
      this.handlerSchedule();
    }, 300000);
  }

  protected readonly StepsEnum = StepsEnum;

  private pollGet(id: string) {
    this.pollService
      .getPollById(id)
      .pipe(take(1),
        finalize(() => {
          this.loadingService.closeLoading();
          this.pollIsLoadingDone = true;
        }))
      .subscribe({
        next: (res) => {
          if (res.success && res.data) {
            this.unitPoll.poll = res.data;
            this.steps.next(StepsEnum.POLL);
          }
        },
      })
  }
}
